import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getUploadUrl } from "src/Common/common";
import { can, downloadImage } from "src/Common/function";
import WOService from "src/Services/API/wo.service";
import UserService from "src/Services/API/user.service";
import AttachmentImageSlider from "src/Pages/WO/Components/attachment/AttachmentImageSlider";
import ScannedDocumentsPanel from "../documents/ScannedDocumentsPanel";
import { getDocumentDisplayName } from "../documents/DocumentScanModals";

// Upload flow discriminator understood by the /attach and scan endpoints.
// 3 = proposal (independent wo_proposal table), 4 = invoice.
const PROPOSAL_UPLOAD_FLOW = 3;

const FILE_ACCEPT =
  "image/*,application/pdf,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const FILE_FOOTER = "Supported formats: JPG, PNG, HEIC, PDF, XLSX";

const isPdfFile = (fileName) => !!fileName && fileName.toLowerCase().endsWith(".pdf");

const isExcelFile = (fileName) => !!fileName && fileName.toLowerCase().endsWith(".xlsx");

const getUploaderName = (proposal) =>
  proposal?.uploadedBy?.name ||
  proposal?.uploadedByCustomerUser?.customer?.companyName ||
  proposal?.uploadedByCustomerUser?.account?.name ||
  null;

function WoProposals({ order, onRefresh }) {
  const currentUser = UserService.getCurrentUser();
  const [sliderIndex, setSliderIndex] = useState(null);

  // Flatten the uploader onto each row so the shared panel can render the
  // "845 KB • Elif Demir • Apr 15, 2026" subline without knowing WO shapes.
  const proposals = useMemo(
    () =>
      (order?.proposalFiles || []).map((proposal) => ({
        ...proposal,
        uploaderName: getUploaderName(proposal),
      })),
    [order?.proposalFiles],
  );

  // Map proposals to the shape AttachmentImageSlider expects. Spreadsheets are
  // excluded — the slider can only render images and PDFs.
  const sliderAttachments = useMemo(
    () =>
      proposals
        .filter((proposal) => !isExcelFile(proposal.fileName))
        .map((proposal) => ({
          id: proposal.id,
          fileName: proposal.fileName,
          isPdf: isPdfFile(proposal.fileName),
          displayName: getDocumentDisplayName(proposal),
          description: proposal.description || "",
          tags: proposal.tags || [],
          createdBy: proposal.uploaderName || "-",
          createdAt: proposal.createdAt,
        })),
    [proposals],
  );

  const handleView = (proposal) => {
    if (!proposal?.fileName) return;
    // No browser preview for spreadsheets — download instead of opening the slider.
    if (isExcelFile(proposal.fileName)) {
      downloadImage(getUploadUrl(proposal.fileName), getDocumentDisplayName(proposal));
      return;
    }
    const index = sliderAttachments.findIndex((attachment) => attachment.id === proposal.id);
    if (index >= 0) setSliderIndex(index);
  };

  const uploadFn = async (files, { documentName }) => {
    const previousIds = new Set(proposals.map((proposal) => proposal.id));
    const metadata = documentName ? [{ documentName }] : null;
    const res = await WOService.uploadFiles(files, order.id, PROPOSAL_UPLOAD_FLOW, currentUser, metadata);
    if (res.status !== 201) {
      toast("Something went wrong");
      return null;
    }
    // The endpoint returns the WO's full proposal list — keep only the new rows.
    return (res.data || []).filter((proposal) => !previousIds.has(proposal.id));
  };

  return (
    <>
      <ScannedDocumentsPanel
        title="Proposals"
        noun="Proposal"
        totalLabel="Work Order's Total Proposal Amount"
        documents={proposals}
        canManage={can("wo:addProposal")}
        canDelete={can("wo:deleteProposal")}
        accept={FILE_ACCEPT}
        footer={FILE_FOOTER}
        getFileUrl={(proposal) => getUploadUrl(proposal.fileName)}
        uploadFn={uploadFn}
        scanFn={(id) => WOService.scanDocument(id, PROPOSAL_UPLOAD_FLOW)}
        confirmFn={(id, amount, sourcePage, sourceLabel) =>
          WOService.confirmDocumentAmount(id, PROPOSAL_UPLOAD_FLOW, amount, sourcePage, sourceLabel)
        }
        deleteFn={(id) => WOService.deleteProposal(order.id, id, PROPOSAL_UPLOAD_FLOW)}
        onRefresh={onRefresh}
        onView={handleView}
      />

      <AttachmentImageSlider
        shown={sliderIndex !== null}
        attachments={sliderAttachments}
        activeIndex={sliderIndex ?? 0}
        onCancel={() => setSliderIndex(null)}
      />
    </>
  );
}

export default WoProposals;
